import { Component, OnInit, OnDestroy } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { Subject, takeUntil } from "rxjs"
import { MatDialog, MatDialogModule } from "@angular/material/dialog"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { MatCardModule } from "@angular/material/card"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { MatTooltipModule } from "@angular/material/tooltip"
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component"
import { LoadingSpinnerComponent } from "../../shared/loading-spinner/loading-spinner.component"
import { StationHeaderComponent } from "../../shared/station-header/station-header.component"
import {
  RatingsService,
  StationRatings,
  RatingUpdate,
  RatingsByDay,
  DayRatings,
  ManualRatingUpdate,
} from "../../services/ratings.service"
import { Station } from "../../models/station.model"
import { StationStoreService } from "../../services/station-store.service"
import { PermissionService } from "../../services/permission.service"
import { AddRatingsDialogComponent } from "./dialogs/add-ratings-dialog.component"
import { FileUploadDialogComponent } from "./dialogs/file-upload-dialog.component"
import { canManageStationConfig, normalizeStationRole } from "../../utils/station-role.util"

@Component({
  selector: "app-manage-ratings",
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    LoadingSpinnerComponent,
    DataLoaderComponent,
    StationHeaderComponent,
  ],
  templateUrl: "./manage-ratings.component.html",
  styleUrls: ["./manage-ratings.component.css"],
})
export class ManageRatingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  stations: Station[] = []
  selectedStation: Station | null = null
  stationRatings: StationRatings | null = null
  /** Station that `stationRatings` was loaded for; edits/deletes must target this station. */
  private ratingsStationId: number | null = null
  loading = false
  loadingRatings = false
  canManage = false

  // Days and hours for display
  days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]
  dayLabels = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  hours = Array.from({ length: 24 }, (_, i) => i)

  // Peak hours highlighting
  peakHours = [6, 7, 8, 9, 15, 16, 17, 18, 19] // Morning and evening rush hours

  constructor(
    private ratingsService: RatingsService,
    private stationStore: StationStoreService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
    private router: Router,
    private permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.permissionService.currentRole$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyPermissionsForSelectedStation()
    })
    this.loadStations()
  }

  private applyPermissionsForSelectedStation(station?: Station | null): void {
    const target = station ?? this.selectedStation
    const role = normalizeStationRole(target?.user_role ?? this.permissionService.getCurrentRole())
    this.canManage = canManageStationConfig(role)
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadStations(): void {
    this.loading = true
    this.stationStore
      .watchStations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stations) => {
          this.stations = stations
          if (this.stations.length > 0 && !this.selectedStation) {
            this.selectedStation = this.stations[0]
            this.ratingsService.setSelectedStation(this.selectedStation)
            this.applyPermissionsForSelectedStation(this.selectedStation)
            this.loadStationRatings()
          }
          this.loading = false
        },
        error: (error) => {
          console.error("Error loading stations:", error)
          this.snackbar.error("Error loading stations", 3000)
          this.loading = false
        },
      })
  }

  onStationChange(station: Station): void {
    this.selectedStation = station
    this.ratingsService.setSelectedStation(this.selectedStation)
    this.applyPermissionsForSelectedStation(station)
    this.loadStationRatings()
  }

  loadStationRatings(): void {
    if (!this.selectedStation) return

    const stationId = this.selectedStation.id
    if (this.ratingsStationId !== stationId) {
      // Don't keep showing (or let the user edit) the previous station's grid.
      this.stationRatings = null
      this.ratingsStationId = null
    }
    this.loadingRatings = true
    this.ratingsService
      .getStationRatings(stationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Drop a slow response for a station the user has already switched away from.
          if (this.selectedStation?.id !== stationId) return
          this.stationRatings = response.data.ratings ? response.data : null
          this.ratingsStationId = this.stationRatings ? stationId : null
          this.loadingRatings = false
        },
        error: (error) => {
          if (this.selectedStation?.id !== stationId) return
          console.error("Error loading station ratings:", error)
          this.snackbar.error("Error loading station ratings", 3000)
          this.stationRatings = null
          this.ratingsStationId = null
          this.loadingRatings = false
        },
      })
  }

  getRating(day: string, hour: number): number {
    if (!this.stationRatings || !this.stationRatings.ratings) return 0
    const dayKey = day.toLowerCase() as keyof RatingsByDay
    const dayRatings = this.stationRatings.ratings[dayKey] as DayRatings
    return dayRatings?.[hour] ?? 0
  }

  getHourDisplay(hour: number): string {
    return this.ratingsService.getHourDisplay(hour)
  }

  getHourShort(hour: number): string {
    if (hour === 0) return "12a"
    if (hour === 12) return "12p"
    if (hour < 12) return `${hour}a`
    return `${hour - 12}p`
  }

  formatRating(rating: number): string {
    return this.ratingsService.formatRating(rating)
  }

  isPeakHour(hour: number): boolean {
    return this.peakHours.includes(hour)
  }

  getRatingIntensity(rating: number): string {
    if (rating >= 500) return "high"
    if (rating >= 200) return "medium"
    if (rating >= 100) return "low"
    return "minimal"
  }

  openAddRatingsDialog(): void {
    if (!this.canManage || !this.selectedStation) return

    const dialogRef = this.dialog.open(AddRatingsDialogComponent, {
      width: "min(1100px, 96vw)",
      maxWidth: "96vw",
      maxHeight: "90vh",
      panelClass: "app-ratings-dialog",
      autoFocus: false,
      data: { station: this.selectedStation },
    })

    dialogRef.afterClosed().subscribe((result: ManualRatingUpdate[] | null) => {
      if (result && this.selectedStation) {
        // Use createRatings for new ratings (POST method)
        this.ratingsService.createRatings(result).subscribe({
          next: () => {
            this.snackbar.success("Ratings added successfully", 3000)
            this.loadStationRatings()
          },
          error: (error) => {
            console.error("Error adding ratings:", error)
            this.snackbar.error("Error adding ratings", 3000)
          },
        })
      }
    })
  }

  openEditRatingsDialog(): void {
    if (!this.canManage || !this.selectedStation || !this.stationRatings) return
    // The grid being edited belongs to ratingsStationId; PUT must go to that station.
    const stationId = this.ratingsStationId
    if (stationId == null || stationId !== this.selectedStation.id) return

    const dialogRef = this.dialog.open(AddRatingsDialogComponent, {
      width: "min(1100px, 96vw)",
      maxWidth: "96vw",
      maxHeight: "90vh",
      panelClass: "app-ratings-dialog",
      autoFocus: false,
      data: {
        station: this.selectedStation,
        currentRatings: this.stationRatings.ratings,
        isEdit: true,
      },
    })

    dialogRef.afterClosed().subscribe((result: RatingUpdate[] | null) => {
      if (result) {
        // Use updateRatings for editing existing ratings (PUT method)
        this.ratingsService.updateRatings(stationId, result).subscribe({
          next: () => {
            this.snackbar.success("Ratings updated successfully", 3000)
            this.loadStationRatings()
          },
          error: (error) => {
            console.error("Error updating ratings:", error)
            this.snackbar.error("Error updating ratings", 3000)
          },
        })
      }
    })
  }

  openFileUploadDialog(): void {
    if (!this.canManage || !this.selectedStation) return

    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: "480px",
      maxWidth: "96vw",
      autoFocus: false,
      panelClass: "app-file-upload-dialog",
      data: { station: this.selectedStation },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.snackbar.success("File uploaded successfully", 3000)
        this.loadStationRatings()
      }
    })
  }

  deleteStationRatings(): void {
    if (!this.canManage || !this.selectedStation) return
    const station = this.selectedStation

    this.confirmDialog
      .open({
        title: "Delete ratings",
        message: `Are you sure you want to delete all ratings data for ${station.name}?`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.ratingsService
          .deleteRatings(station.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.snackbar.success("Ratings deleted successfully", 3000)
              if (this.selectedStation?.id === station.id) {
                this.stationRatings = null
                this.ratingsStationId = null
              }
            },
            error: (error) => {
              console.error("Error deleting ratings:", error)
              this.snackbar.error("Error deleting ratings", 3000)
            },
          })
      })
  }

  refreshRatings(): void {
    this.loadStationRatings()
  }

  goBack(): void {
    this.router.navigate(["/userhome"])
  }
}
